/**
 * The four buckets a document's line items are added and shown in.
 *
 * A quote is written a section at a time — services, materials, equipment,
 * then memberships — so each section owns its own
 * search that only offers items of its kind. One search across the whole catalog
 * makes you disambiguate by name what the section already knows.
 */

export type LineSectionId = 'services' | 'materials' | 'equipment' | 'memberships';

export interface LineSection {
  id: LineSectionId;
  title: string;
  /** What the section's own picker is allowed to find. */
  allowedTypes: string[];
  addLabel: string;
  placeholder: string;
}

/**
 * Bundles sit with services: a bundle containing any service IS a service for
 * pricing and tax, and an all-material bundle is still sold as one line rather
 * than as the parts it contains.
 */
export const LINE_SECTIONS: LineSection[] = [
  {
    id: 'services',
    title: 'Services',
    // NOT-628 — fees belong here too. `sectionForType` already RENDERS a fee
    // line in this section, but the picker could not FIND one, so a plan whose
    // benefit waives a fee item had no way to put that fee on the document and
    // the waiver never fired.
    allowedTypes: ['SERVICE', 'BUNDLE', 'FEE'],
    addLabel: 'Add services',
    placeholder: 'Search services and bundles…',
  },
  {
    id: 'materials',
    title: 'Materials',
    allowedTypes: ['MATERIAL'],
    addLabel: 'Add materials',
    placeholder: 'Search materials…',
  },
  {
    id: 'equipment',
    title: 'Equipment',
    allowedTypes: ['EQUIPMENT'],
    addLabel: 'Add equipment',
    placeholder: 'Search equipment…',
  },
  {
    id: 'memberships',
    title: 'Memberships',
    allowedTypes: ['MEMBERSHIP'],
    addLabel: 'Add memberships',
    placeholder: 'Search memberships…',
  },
];

/**
 * Which section a line belongs to, by its type.
 *
 * Everything unrecognised — fees and custom one-off lines — falls to services,
 * because a document must render every line it holds. A line that matched no
 * section would vanish from the editor while still being billed.
 */
export function sectionForType(type: string | null | undefined): LineSectionId {
  switch ((type ?? '').toUpperCase()) {
    case 'MATERIAL':
      return 'materials';
    case 'EQUIPMENT':
      return 'equipment';
    default:
      return 'services';
  }
}

/**
 * Which estimate-editor section should display a line.
 *
 * Memberships keep service-like pricing semantics everywhere else (no resale
 * cost/markup fields), but the estimate editor gives them a dedicated picker
 * and visual section.
 */
export function estimateSectionForType(type: string | null | undefined): LineSectionId {
  if (['MEMBERSHIP', 'MEMBERSHIP_SETUP'].includes((type ?? '').toUpperCase())) return 'memberships';
  return sectionForType(type);
}
